import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import './ReleaseNotes.css';
import AddButtonWithPlus from '../../../components/common/AddButton/AddButtonWithPlus';
import InputBoxSmall from '../../../components/common/InputBox/InputBoxSmall';
import IconUploadButton from '../../../components/common/InputBox/UploadIconBox';
import { updateReleaseNote } from '../../../services/ReleaseNotes/Releasenotesapi';
import ErrorNotification from '../../../components/common/AlertBox/ErrorNotification';
import { SuccessAlert } from '../../../components/common/AlertBox/GreenAlert';

const EditReleaseNotes = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const note = location.state?.note;

    // ── Basic fields — pre-populated from note ────────────────────
    const [version, setVersion] = useState(note?.version ?? '');
    const [releaseTitle, setReleaseTitle] = useState(note?.title ?? '');
    const [releaseDate, setReleaseDate] = useState(note?.release_date ?? '');
    const [releaseStatus, setReleaseStatus] = useState(note?.status ?? 'draft');

    // ── Submission state ──────────────────────────────────────────
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    // ── Features — pre-populated from note ───────────────────────
    const [features, setFeatures] = useState(
        (note?.features ?? []).map(f => ({ ...f, icon: f.image ?? null }))
    );
    const [showFeatureInput, setShowFeatureInput] = useState(false);
    const [newFeatureName, setNewFeatureName] = useState('');
    const [newFeatureDescription, setNewFeatureDescription] = useState('');
    const [newFeatureIcon, setNewFeatureIcon] = useState(null);
    const [editFeatureId, setEditFeatureId] = useState(null);
    const [editFeatureName, setEditFeatureName] = useState('');
    const [editFeatureDescription, setEditFeatureDescription] = useState('');
    const [editFeatureIcon, setEditFeatureIcon] = useState(null);

    // ── Improvements — pre-populated from note ────────────────────
    const [improvements, setImprovements] = useState(
        (note?.improvements ?? []).map(i => ({ ...i, name: i.text }))
    );
    const [showImprovementInput, setShowImprovementInput] = useState(false);
    const [newImprovementName, setNewImprovementName] = useState('');
    const [newImprovementDescription, setNewImprovementDescription] = useState('');

    const [editImprovementId, setEditImprovementId] = useState(null);
    const [editImprovementName, setEditImprovementName] = useState('');
    const [editImprovementDescription, setEditImprovementDescription] = useState('');

    // ── Bugs — pre-populated from note ───────────────────────────
    const [bugs, setBugs] = useState(
        (note?.bug_fixes ?? []).map(b => ({ ...b, name: b.text }))
    );
    const [showBugInput, setShowBugInput] = useState(false);
    const [newBugName, setNewBugName] = useState('');
    const [newBugDescription, setNewBugDescription] = useState('');

    const [editBugId, setEditBugId] = useState(null);
    const [editBugName, setEditBugName] = useState('');
    const [editBugDescription, setEditBugDescription] = useState('');

    // ── Guard ─────────────────────────────────────────────────────
    if (!note) {
        return (
            <div className='whole-container'>
                <div className='pagename-container'>
                    <span className='common-text fts-16'>No release note data found.</span>
                    <button className='cancel-btn-rn bg-white' onClick={() => navigate('/release-notes')}>
                        Back
                    </button>
                </div>
            </div>
        );
    }

    // ── Validation ────────────────────────────────────────────────
    const validateData = () => {
        const errors = {};
        if (!version.trim()) errors.version = 'Version is required';
        if (!releaseTitle.trim()) errors.title = 'Release title is required';

        if (features.length === 0 && improvements.length === 0 && bugs.length === 0) {
            errors.content = 'At least one feature or improvement or bug fix is required';
        }

        return { isValid: Object.keys(errors).length === 0, errors };
    };

    // ── Payload builders ──────────────────────────────────────────
    const hasFileIcons = features.some(f => f.icon instanceof File);

    const buildFormData = (status) => {
        const formData = new FormData();
        formData.append('version', version);
        formData.append('title', releaseTitle);
        if (releaseDate) formData.append('release_date', releaseDate);
        formData.append('status', status);
        formData.append('features', JSON.stringify(features.map(f => ({ name: f.name, description: f.description }))));
        features.forEach((f, index) => {
            if (f.icon instanceof File) formData.append(`feature_image_${index}`, f.icon);
        });
        formData.append('improvements', JSON.stringify(improvements.map(i => ({ text: i.name, description: i.description }))));
        formData.append('bug_fixes', JSON.stringify(bugs.map(b => ({ text: b.name, description: b.description }))));
        return formData;
    };

    const buildJsonPayload = (status) => ({
        version,
        title: releaseTitle,
        release_date: releaseDate || null,
        status,
        features: features.map(f => ({ name: f.name, description: f.description })),
        improvements: improvements.map(i => ({ text: i.name, description: i.description })),
        bug_fixes: bugs.map(b => ({ text: b.name, description: b.description })),
    });

    // ── Save Draft ────────────────────────────────────────────────
    const handleSaveDraft = async () => {
        const { isValid, errors } = validateData();
        if (!isValid) { setFormErrors(errors); return; }
        if (isSubmitting) return;

        setIsSubmitting(true);

        try {
            const payload = hasFileIcons
                ? buildFormData('draft')
                : buildJsonPayload('draft');

            const result = await updateReleaseNote(note.id, payload);

            if (result) {
                SuccessAlert('Draft saved successfully.');

                navigate(`/release-notes/detail/${note.id}`, {
                    state: {
                        note: {
                            ...note,
                            version,
                            title: releaseTitle,
                            release_date: releaseDate,
                            status: 'draft',
                            features,
                            improvements,
                            bug_fixes: bugs,
                        }
                    }
                });
            }

        } catch (error) {
            ErrorNotification(error.message); // swap for showAlert(error.message) if you have it
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Publish ───────────────────────────────────────────────────
    const handlePublish = async () => {
        const { isValid, errors } = validateData();
        if (!isValid) { setFormErrors(errors); return; }
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            const payload = hasFileIcons ? buildFormData('published') : buildJsonPayload('published');
            const result = await updateReleaseNote(note.id, payload);
            if (result) {
                SuccessAlert('Release note published successfully!');

                navigate(`/release-notes/detail/${note.id}`, {
                    state: {
                        note: {
                            ...note,
                            version,
                            title: releaseTitle,
                            release_date: releaseDate,
                            status: 'published',
                            features,
                            improvements,
                            bug_fixes: bugs,
                        }
                    }
                });
            }
        } catch (error) {
            ErrorNotification(error.message); // swap for showAlert(error.message) if you have it
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddFeature = () => {
        if (!newFeatureName.trim()) return;
        setFeatures(prev => [...prev, {
            id: Date.now(),
            name: newFeatureName.trim(),
            description: newFeatureDescription.trim(),
            icon: newFeatureIcon
        }]);
        setNewFeatureName(''); setNewFeatureDescription(''); setNewFeatureIcon(null); setShowFeatureInput(false);
    };

    const handleAddImprovement = () => {
        if (!newImprovementName.trim()) return;
        setImprovements(prev => [...prev, {
            id: Date.now(),
            name: newImprovementName.trim(),
            description: newImprovementDescription.trim()
        }]);
        setNewImprovementName(''); setNewImprovementDescription(''); setShowImprovementInput(false);
    };

    const handleAddBug = () => {
        if (!newBugName.trim()) return;
        setBugs(prev => [...prev, {
            id: Date.now(),
            name: newBugName.trim(),
            description: newBugDescription.trim()
        }]);
        setNewBugName(''); setNewBugDescription(''); setShowBugInput(false);
    };

    const handleSaveEditFeature = () => {
        if (!editFeatureName.trim()) return;
        setFeatures(prev => prev.map(f =>
            f.id === editFeatureId
                ? { ...f, name: editFeatureName.trim(), description: editFeatureDescription.trim(), icon: editFeatureIcon }
                : f
        ));
        setEditFeatureId(null);
    };

    const handleSaveEditImprovement = () => {
        if (!editImprovementName.trim()) return;
        setImprovements(prev => prev.map(i =>
            i.id === editImprovementId
                ? { ...i, name: editImprovementName.trim(), description: editImprovementDescription.trim() }
                : i
        ));
        setEditImprovementId(null);
    };

    const handleSaveEditBug = () => {
        if (!editBugName.trim()) return;
        setBugs(prev => prev.map(b =>
            b.id === editBugId
                ? { ...b, name: editBugName.trim(), description: editBugDescription.trim() }
                : b
        ));
        setEditBugId(null);
    };

    return (
        <div className='whole-container'>
            <div className='top-heading'>
                <PageHeader pageName=" Engagement / Release Notes /" nestedpageName="Edit" linkTo="/release-notes" />
            </div>
            <div className='pagename-container'>
                <div className='page-name'>
                    <h1 className='common-text fts-22 ftw-6 blue'>Edit Release Note</h1>
                </div>
            </div>
            <div className='table-container'>
                <div className='add-release-note-container'>

                    {/* ── ERROR BANNER ── */}
                    <div>
                        <ErrorNotification
                            errors={formErrors}
                            width="100%"
                            marginBottom="1rem"
                        />
                    </div>

                    {/* ── BASIC INFO ── */}
                    <div className='release-basic-container'>
                        <div className='release-basic-header'>
                            <span className='release-header-title'>Basic Information</span>
                        </div>
                        <div className='release-basic-content'>
                            <div className='release-version-container'>
                                <InputBoxSmall
                                    label="Version"
                                    defaultValue={version}
                                    onChange={(e) => {
                                        setVersion(e.target.value);
                                        if (formErrors.version) setFormErrors(prev => { const { version: _, ...rest } = prev; return rest; });
                                    }}
                                />
                            </div>
                            <div className='release-basic-description-container'>
                                <InputBoxSmall
                                    label="Release title"
                                    defaultValue={releaseTitle}
                                    onChange={(e) => {
                                        setReleaseTitle(e.target.value);
                                        if (formErrors.title) setFormErrors(prev => { const { title: _, ...rest } = prev; return rest; });
                                    }}
                                />
                                <InputBoxSmall
                                    label="Release date"
                                    type='small-date'
                                    defaultValue={releaseDate}
                                    onChange={(e) => setReleaseDate(e.target.value)}
                                />
                                <InputBoxSmall
                                    label="Status"
                                    defaultValue={releaseStatus}
                                    readOnly
                                />
                            </div>
                            <div className='release-basic-bt-text'>
                                <span>This title is for internal use only and will not be shown to users.</span>
                            </div>
                        </div>
                    </div>

                    {/* ── FEATURE INFO ───────────────────────────────────────── */}
                    <div className='release-feature-container'>
                        <div className='release-feature-header'>
                            <span className='release-header-title'>Feature Information</span>
                        </div>
                        <div className='release-feature-content-container'>
                            <div className='release-feature-titles release-feature-grid'>
                                <span>Icon</span>
                                <span>Feature Name</span>
                                <span>Description</span>
                                <span></span>
                            </div>
                            <div className='release-feature-content'>
                                {features.map((item) => (
                                    <div className='release-feature-row release-feature-grid' key={item.id}>
                                        {editFeatureId === item.id ? (
                                            <>
                                                <IconUploadButton
                                                    icon={editFeatureIcon}
                                                    onIconChange={(icon) => setEditFeatureIcon(icon)}
                                                />
                                                <InputBoxSmall
                                                    label="Feature Name"
                                                    defaultValue={editFeatureName}
                                                    onChange={(e) => setEditFeatureName(e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEditFeature(); }}
                                                    maxLength={30}
                                                />
                                                <InputBoxSmall
                                                    label="Description"
                                                    defaultValue={editFeatureDescription}
                                                    onChange={(e) => setEditFeatureDescription(e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEditFeature(); }}
                                                />
                                                <div className="release-close-icon" onClick={handleSaveEditFeature}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <path d="M13.8 3L2.79999 14" stroke="#FA0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                        <path d="M2.79999 3L13.8 14" stroke="#FF0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <IconUploadButton
                                                    icon={item.icon}
                                                    onIconChange={(newIcon) =>
                                                        setFeatures(prev => prev.map(f => f.id === item.id ? { ...f, icon: newIcon } : f))
                                                    }
                                                />
                                                <div className='release-box-item' title={item.name}>{item.name}</div>
                                                <div className='release-box-item' title={item.description}>{item.description}</div>
                                                <div className='release-feature-content-right-row'>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            setEditFeatureId(item.id);
                                                            setEditFeatureName(item.name);
                                                            setEditFeatureDescription(item.description);
                                                            setEditFeatureIcon(item.icon ?? null);
                                                        }}
                                                    >
                                                        <path d="M8 2H3.33333C2.97971 2 2.64057 2.14048 2.39052 2.39052C2.14048 2.64057 2 2.97971 2 3.33333V12.6667C2 13.0203 2.14048 13.3594 2.39052 13.6095C2.64057 13.8595 2.97971 14 3.33333 14H12.6667C13.0203 14 13.3594 13.8595 13.6095 13.6095C13.8595 13.3594 14 13.0203 14 12.6667V8" stroke="#094C80" strokeWidth="1.06667" strokeLinecap="round" strokeLinejoin="round" />
                                                        <path d="M12.25 1.74917C12.5152 1.48396 12.8749 1.33496 13.25 1.33496C13.6251 1.33496 13.9848 1.48396 14.25 1.74917C14.5152 2.01439 14.6642 2.3741 14.6642 2.74917C14.6642 3.12425 14.5152 3.48396 14.25 3.74917L8.24133 9.75851C8.08303 9.91667 7.88747 10.0325 7.67267 10.0952L5.75733 10.6552C5.69997 10.6719 5.63916 10.6729 5.58127 10.6581C5.52339 10.6432 5.47055 10.6131 5.4283 10.5709C5.38604 10.5286 5.35592 10.4758 5.34109 10.4179C5.32626 10.36 5.32727 10.2992 5.344 10.2418L5.904 8.32651C5.96702 8.11187 6.08302 7.91655 6.24133 7.75851L12.25 1.74917Z" stroke="#094C80" strokeWidth="1.06667" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ cursor: 'pointer' }}
                                                        onClick={() => setFeatures(prev => prev.filter(f => f.id !== item.id))}
                                                    >
                                                        <path d="M13.8 3L2.79999 14" stroke="#FA0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                        <path d="M2.79999 3L13.8 14" stroke="#FF0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}

                                {showFeatureInput && (
                                    <div className='release-feature-row release-feature-grid'>
                                        <IconUploadButton icon={newFeatureIcon} onIconChange={(icon) => setNewFeatureIcon(icon)} />
                                        <InputBoxSmall
                                            label="Feature Name"
                                            value={newFeatureName}
                                            onChange={(e) => setNewFeatureName(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleAddFeature(); }}
                                            maxLength={30}
                                        />
                                        <InputBoxSmall
                                            label="Description"
                                            value={newFeatureDescription}
                                            onChange={(e) => setNewFeatureDescription(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleAddFeature(); }}
                                        />
                                        <div className="release-close-icon" onClick={() => { setNewFeatureName(''); setNewFeatureDescription(''); setNewFeatureIcon(null); setShowFeatureInput(false); }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <path d="M13.8 3L2.79999 14" stroke="#FA0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M2.79999 3L13.8 14" stroke="#FF0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    </div>
                                )}
                                <div className='release-feature-add'>
                                    <AddButtonWithPlus
                                        onClick={() => {
                                            if (showFeatureInput) {
                                                handleAddFeature();
                                            } else {
                                                setShowFeatureInput(true);
                                            }
                                        }}
                                        disabled={false}
                                    />
                                </div>
                            </div>
                        </div>

                        <hr />

                        {/* ── IMPROVEMENTS ────────────────────────────── */}
                        <div className='release-feature-container' style={{ marginTop: '1.5rem' }}>
                            <div className='release-feature-header'>
                                <span className='release-header-title'>Improvements</span>
                            </div>
                            <div className='release-feature-content-container'>
                                <div className='release-feature-titles release-improvement-grid'>
                                    <span>Improvement Title</span>
                                    <span>Description</span>
                                    <span></span>
                                </div>

                                <div className='release-feature-content'>
                                    {improvements.map((item) => (
                                        <div className='release-feature-row release-improvement-grid' key={item.id}>
                                            {editImprovementId === item.id ? (
                                                <>
                                                    <InputBoxSmall
                                                        label="Title"
                                                        defaultValue={editImprovementName}
                                                        onChange={(e) => setEditImprovementName(e.target.value)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEditImprovement(); }}
                                                        maxLength={60}
                                                    />
                                                    <InputBoxSmall
                                                        label="Description"
                                                        defaultValue={editImprovementDescription}
                                                        onChange={(e) => setEditImprovementDescription(e.target.value)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEditImprovement(); }}
                                                    />
                                                    <div className="release-close-icon" onClick={handleSaveEditImprovement}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <path d="M13.8 3L2.79999 14" stroke="#FA0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M2.79999 3L13.8 14" stroke="#FF0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className='release-box-item' title={item.name}>{item.name}</div>
                                                    <div className='release-box-item' title={item.description}>{item.description}</div>
                                                    <div className='release-feature-content-right-row'>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ cursor: 'pointer' }}
                                                            onClick={() => {
                                                                setEditImprovementId(item.id);
                                                                setEditImprovementName(item.name);
                                                                setEditImprovementDescription(item.description);
                                                            }}
                                                        >
                                                            <path d="M8 2H3.33333C2.97971 2 2.64057 2.14048 2.39052 2.39052C2.14048 2.64057 2 2.97971 2 3.33333V12.6667C2 13.0203 2.14048 13.3594 2.39052 13.6095C2.64057 13.8595 2.97971 14 3.33333 14H12.6667C13.0203 14 13.3594 13.8595 13.6095 13.6095C13.8595 13.3594 14 13.0203 14 12.6667V8" stroke="#094C80" strokeWidth="1.06667" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M12.25 1.74917C12.5152 1.48396 12.8749 1.33496 13.25 1.33496C13.6251 1.33496 13.9848 1.48396 14.25 1.74917C14.5152 2.01439 14.6642 2.3741 14.6642 2.74917C14.6642 3.12425 14.5152 3.48396 14.25 3.74917L8.24133 9.75851C8.08303 9.91667 7.88747 10.0325 7.67267 10.0952L5.75733 10.6552C5.69997 10.6719 5.63916 10.6729 5.58127 10.6581C5.52339 10.6432 5.47055 10.6131 5.4283 10.5709C5.38604 10.5286 5.35592 10.4758 5.34109 10.4179C5.32626 10.36 5.32727 10.2992 5.344 10.2418L5.904 8.32651C5.96702 8.11187 6.08302 7.91655 6.24133 7.75851L12.25 1.74917Z" stroke="#094C80" strokeWidth="1.06667" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ cursor: 'pointer' }}
                                                            onClick={() => setImprovements(prev => prev.filter(i => i.id !== item.id))}
                                                        >
                                                            <path d="M13.8 3L2.79999 14" stroke="#FA0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M2.79999 3L13.8 14" stroke="#FF0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                    {showImprovementInput && (
                                        <div className='release-feature-row release-improvement-grid'>
                                            <InputBoxSmall
                                                label="Improvement Title"
                                                value={newImprovementName}
                                                onChange={(e) => setNewImprovementName(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') handleAddImprovement(); }}
                                                maxLength={60}
                                            />
                                            <InputBoxSmall
                                                label="Description"
                                                value={newImprovementDescription}
                                                onChange={(e) => setNewImprovementDescription(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') handleAddImprovement(); }}
                                            />
                                            <div className="release-close-icon" onClick={() => { setNewImprovementName(''); setNewImprovementDescription(''); setShowImprovementInput(false); }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                    <path d="M13.8 3L2.79999 14" stroke="#FA0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M2.79999 3L13.8 14" stroke="#FF0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                    )}
                                    <div className='release-feature-add'>
                                        <AddButtonWithPlus
                                            onClick={() => showImprovementInput ? handleAddImprovement() : setShowImprovementInput(true)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ==================== BUG FIXES ==================== */}
                        <div className='release-feature-container' style={{ marginTop: '1.5rem' }}>
                            <div className='release-feature-header'>
                                <span className='release-header-title'>Bug Fixes</span>
                            </div>
                            <div className='release-feature-content-container'>
                                <div className='release-feature-titles release-improvement-grid'>
                                    <span>Bug Title</span>
                                    <span>Description</span>
                                    <span></span>
                                </div>

                                <div className='release-feature-content'>
                                    {bugs.map((item) => (
                                        <div className='release-feature-row release-improvement-grid' key={item.id}>
                                            {editBugId === item.id ? (
                                                <>
                                                    <InputBoxSmall
                                                        label="Title"
                                                        defaultValue={editBugName}
                                                        onChange={(e) => setEditBugName(e.target.value)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEditBug(); }}
                                                        maxLength={60}
                                                    />
                                                    <InputBoxSmall
                                                        label="Description"
                                                        defaultValue={editBugDescription}
                                                        onChange={(e) => setEditBugDescription(e.target.value)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEditBug(); }}
                                                    />
                                                    <div className="release-close-icon" onClick={handleSaveEditBug}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <path d="M13.8 3L2.79999 14" stroke="#FA0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M2.79999 3L13.8 14" stroke="#FF0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className='release-box-item' title={item.name}>{item.name}</div>
                                                    <div className='release-box-item' title={item.description}>{item.description}</div>
                                                    <div className='release-feature-content-right-row'>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ cursor: 'pointer' }}
                                                            onClick={() => {
                                                                setEditBugId(item.id);
                                                                setEditBugName(item.name);
                                                                setEditBugDescription(item.description);
                                                            }}
                                                        >
                                                            <path d="M8 2H3.33333C2.97971 2 2.64057 2.14048 2.39052 2.39052C2.14048 2.64057 2 2.97971 2 3.33333V12.6667C2 13.0203 2.14048 13.3594 2.39052 13.6095C2.64057 13.8595 2.97971 14 3.33333 14H12.6667C13.0203 14 13.3594 13.8595 13.6095 13.6095C13.8595 13.3594 14 13.0203 14 12.6667V8" stroke="#094C80" strokeWidth="1.06667" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M12.25 1.74917C12.5152 1.48396 12.8749 1.33496 13.25 1.33496C13.6251 1.33496 13.9848 1.48396 14.25 1.74917C14.5152 2.01439 14.6642 2.3741 14.6642 2.74917C14.6642 3.12425 14.5152 3.48396 14.25 3.74917L8.24133 9.75851C8.08303 9.91667 7.88747 10.0325 7.67267 10.0952L5.75733 10.6552C5.69997 10.6719 5.63916 10.6729 5.58127 10.6581C5.52339 10.6432 5.47055 10.6131 5.4283 10.5709C5.38604 10.5286 5.35592 10.4758 5.34109 10.4179C5.32626 10.36 5.32727 10.2992 5.344 10.2418L5.904 8.32651C5.96702 8.11187 6.08302 7.91655 6.24133 7.75851L12.25 1.74917Z" stroke="#094C80" strokeWidth="1.06667" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ cursor: 'pointer' }}
                                                            onClick={() => setBugs(prev => prev.filter(b => b.id !== item.id))}
                                                        >
                                                            <path d="M13.8 3L2.79999 14" stroke="#FA0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M2.79999 3L13.8 14" stroke="#FF0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                    {showBugInput && (
                                        <div className='release-feature-row release-improvement-grid'>
                                            <InputBoxSmall
                                                label="Bug Title"
                                                value={newBugName}
                                                onChange={(e) => setNewBugName(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') handleAddBug(); }}
                                                maxLength={60}
                                            />
                                            <InputBoxSmall
                                                label="Description"
                                                value={newBugDescription}
                                                onChange={(e) => setNewBugDescription(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') handleAddBug(); }}
                                            />
                                            <div className="release-close-icon" onClick={() => { setNewBugName(''); setNewBugDescription(''); setShowBugInput(false); }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                    <path d="M13.8 3L2.79999 14" stroke="#FA0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M2.79999 3L13.8 14" stroke="#FF0000" strokeWidth="1.07" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                    )}
                                    <div className='release-feature-add'>
                                        <AddButtonWithPlus
                                            onClick={() => showBugInput ? handleAddBug() : setShowBugInput(true)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── ACTION BUTTONS ─────────────────────────────────── */}
                        <div className='release-final-btn-container'>
                            <button
                                className='cancel-btn-rn bg-white'
                                onClick={() => navigate(-1)}
                            >
                                Back
                            </button>
                            <button
                                className='save-draft-btn-rn'
                                onClick={handleSaveDraft}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Saving...' : 'Save Draft'}
                            </button>
                            <button
                                className='release-final-btn'
                                onClick={handlePublish}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Publishing...' : 'Publish Release Note'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditReleaseNotes;